import React, { useState, useEffect } from "react";
import { Box, Paper, TextField, Button, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "../../config/axios";
import { Link } from "react-router-dom";

function EmailVerify() {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const handleInputChange = (e) => {
    setEmail(e.target.value);
    setFormError("");
    setErrorMessage("");
    setSuccessMessage("");
  };

  useEffect(() => {
    if (errorMessage || successMessage || formError) {
      const timer = setTimeout(() => {
        setFormError("");
        setErrorMessage("");
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage, formError]);

  const validateEmail = () => {
    const emailRegex =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    if (!email || !emailRegex.test(email)) {
      setFormError("Valid email is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await axios.post("/forgot-password", { email });
      setSuccessMessage("Verification email sent! Please check your inbox.");
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || "Failed to send verification email."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          background: "rgba(255,255,255,0.95)",
          borderRadius: "16px",
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: "bold",
            background: "#26A69A",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 4,
          }}
        >
          Email Verification
        </Typography>
        {errorMessage && (
          <div
            style={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            <ErrorIcon style={{ marginRight: "8px" }} />
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div
            style={{
              background: "#4CAF50",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            {successMessage}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={handleInputChange}
            margin="normal"
            error={!!formError}
            helperText={
              formError && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#FF6F61",
                  }}
                >
                  <ErrorIcon style={{ fontSize: "16px", marginRight: "4px" }} />
                  {formError}
                </span>
              )
            }
            autoFocus
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                background: "#F5F5F5",
                transition: "all 0.3s",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#26A69A",
                boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
              },
              "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
              "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              background: "#26A69A",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.5,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.01)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                background: "#26A69A",
              },
              "&:disabled": { background: "grey.500", transform: "none" },
            }}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Verification Email"}
          </Button>
        </form>
        <Link to="/login" style={{ textDecoration: "none", cursor: "pointer" }}>
          <Typography
            gutterBottom
            align="center"
            sx={{
              fontWeight: "bold",
              background: "#26A69A",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Back to login?
          </Typography>
        </Link>
      </Paper>
    </Box>
  );
}

export default EmailVerify;
